import { type ReactNode } from 'react'
import { Info, Lightbulb, AlertTriangle, Terminal, ExternalLink } from 'lucide-react'

// ── Typography ─────────────────────────────────────────────────────────────────

export function H1({ id, children }: { id?: string; children: ReactNode }) {
  return <h1 id={id} className="text-3xl font-bold text-slate-900 mb-3 mt-0">{children}</h1>
}

export function H2({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <h2 id={id} className="text-xl font-semibold text-slate-900 mt-10 mb-3 pb-2 border-b border-slate-100 group flex items-center gap-2">
      {children}
      {id && (
        <a href={`#${id}`} className="opacity-0 group-hover:opacity-50 text-slate-400 hover:text-slate-600 transition-opacity text-base font-normal">#</a>
      )}
    </h2>
  )
}

export function H3({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <h3 id={id} className="text-base font-semibold text-slate-900 mt-7 mb-2">{children}</h3>
  )
}

export function P({ children }: { children: ReactNode }) {
  return <p className="text-slate-600 leading-relaxed mb-4">{children}</p>
}

export function Lead({ children }: { children: ReactNode }) {
  return <p className="text-lg text-slate-600 leading-relaxed mb-6">{children}</p>
}

export function UL({ children }: { children: ReactNode }) {
  return <ul className="list-disc pl-5 mb-4 space-y-1.5 text-slate-600">{children}</ul>
}

export function OL({ children }: { children: ReactNode }) {
  return <ol className="list-decimal pl-5 mb-4 space-y-1.5 text-slate-600">{children}</ol>
}

export function LI({ children }: { children: ReactNode }) {
  return <li className="leading-relaxed">{children}</li>
}

export function Code({ children }: { children: ReactNode }) {
  return <code className="font-mono text-[0.88em] bg-slate-100 text-blue-700 px-1.5 py-0.5 rounded">{children}</code>
}

export function A({ href, children }: { href: string; children: ReactNode }) {
  const external = href.startsWith('http')
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className="text-blue-600 hover:text-blue-700 underline underline-offset-2 inline-flex items-center gap-0.5"
    >
      {children}
      {external && <ExternalLink size={12} className="inline ml-0.5 opacity-60" />}
    </a>
  )
}

// ── Callouts ───────────────────────────────────────────────────────────────────

type CalloutType = 'info' | 'tip' | 'warning' | 'note'

const calloutStyles: Record<CalloutType, { bg: string; border: string; icon: ReactNode; label: string }> = {
  info:    { bg: 'bg-blue-50',   border: 'border-blue-200',  icon: <Info size={15} className="text-blue-500 shrink-0 mt-0.5" />,          label: 'Info' },
  tip:     { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: <Lightbulb size={15} className="text-emerald-500 shrink-0 mt-0.5" />, label: 'Tip' },
  warning: { bg: 'bg-amber-50',  border: 'border-amber-200', icon: <AlertTriangle size={15} className="text-amber-500 shrink-0 mt-0.5" />, label: 'Warning' },
  note:    { bg: 'bg-slate-50',  border: 'border-slate-200', icon: <Info size={15} className="text-slate-400 shrink-0 mt-0.5" />,          label: 'Note' },
}

export function Callout({ type = 'info', title, children }: { type?: CalloutType; title?: string; children: ReactNode }) {
  const s = calloutStyles[type]
  return (
    <div className={`flex gap-3 rounded-lg border px-4 py-3 mb-5 ${s.bg} ${s.border}`}>
      {s.icon}
      <div className="flex-1 min-w-0">
        {title && <p className="font-semibold text-slate-800 text-sm mb-0.5">{title}</p>}
        {!title && <span className="font-semibold text-slate-800 text-sm">{s.label}: </span>}
        <span className="text-sm text-slate-700 leading-relaxed">{children}</span>
      </div>
    </div>
  )
}

// ── Code block ─────────────────────────────────────────────────────────────────

export function CodeBlock({ language = 'bash', title, children }: { language?: string; title?: string; children: string }) {
  return (
    <div className="mb-5 rounded-lg overflow-hidden border border-slate-200 shadow-sm">
      {(title || language) && (
        <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <Terminal size={13} className="text-slate-400" />
            <span className="text-[11px] font-mono text-slate-400 uppercase tracking-wider">{title || language}</span>
          </div>
        </div>
      )}
      <pre className="bg-[#1e2433] text-slate-200 p-4 overflow-x-auto text-[13px] leading-relaxed font-mono">
        <code>{children.trim()}</code>
      </pre>
    </div>
  )
}

// ── Screenshot placeholder ─────────────────────────────────────────────────────

export function Screenshot({ src, alt, caption }: { src?: string; alt: string; caption?: string }) {
  return (
    <figure className="mb-6">
      {src ? (
        <img src={src} alt={alt} className="rounded-lg border border-slate-200 shadow-sm w-full" />
      ) : (
        <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center py-12 px-6 text-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-slate-400">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="m21 15-5-5L5 21" />
            </svg>
          </div>
          <p className="text-sm text-slate-400 font-medium">{alt}</p>
        </div>
      )}
      {caption && (
        <figcaption className="text-center text-xs text-slate-400 mt-2 italic">{caption}</figcaption>
      )}
    </figure>
  )
}

// ── Step-by-step guide ─────────────────────────────────────────────────────────

export function Steps({ children }: { children: ReactNode }) {
  return <div className="mb-6 space-y-0">{children}</div>
}

export function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <div className="flex gap-4 pb-6 relative">
      <div className="flex flex-col items-center shrink-0">
        <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold shrink-0 z-10">
          {n}
        </div>
        <div className="flex-1 w-px bg-slate-200 mt-1" />
      </div>
      <div className="flex-1 pb-2">
        <h4 className="font-semibold text-slate-900 mb-2 mt-1">{title}</h4>
        <div className="text-slate-600 text-sm leading-relaxed space-y-2">{children}</div>
      </div>
    </div>
  )
}

// ── Property / configuration table ────────────────────────────────────────────

export interface PropDef {
  name: string
  type: string
  required?: boolean
  default?: string
  description: string
}

export function PropTable({ props }: { props: PropDef[] }) {
  return (
    <div className="mb-6 overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Field</th>
            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Type</th>
            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Required</th>
            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Default</th>
            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {props.map(p => (
            <tr key={p.name} className="hover:bg-slate-50 transition-colors">
              <td className="px-4 py-2.5 font-mono text-blue-700 text-[13px] whitespace-nowrap">{p.name}</td>
              <td className="px-4 py-2.5 font-mono text-slate-500 text-[12px] whitespace-nowrap">{p.type}</td>
              <td className="px-4 py-2.5">
                {p.required
                  ? <span className="text-[11px] bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5 rounded font-medium">required</span>
                  : <span className="text-[11px] text-slate-400">optional</span>}
              </td>
              <td className="px-4 py-2.5 font-mono text-slate-400 text-[12px]">{p.default ?? '—'}</td>
              <td className="px-4 py-2.5 text-slate-600 text-[13px]">{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Tag / badge ────────────────────────────────────────────────────────────────

type TagColor = 'blue' | 'green' | 'amber' | 'red' | 'purple' | 'slate'
const tagColors: Record<TagColor, string> = {
  blue:   'bg-blue-50 text-blue-700 border-blue-200',
  green:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber:  'bg-amber-50 text-amber-700 border-amber-200',
  red:    'bg-red-50 text-red-700 border-red-200',
  purple: 'bg-purple-50 text-purple-700 border-purple-200',
  slate:  'bg-slate-100 text-slate-600 border-slate-200',
}

export function Tag({ color = 'slate', children }: { color?: TagColor; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center text-[11px] font-medium border px-1.5 py-0.5 rounded ${tagColors[color]}`}>
      {children}
    </span>
  )
}

// ── Grid layout ────────────────────────────────────────────────────────────────

export function Grid({ cols = 2, children }: { cols?: 2 | 3; children: ReactNode }) {
  return (
    <div className={`grid gap-4 mb-6 ${cols === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`}>
      {children}
    </div>
  )
}

export function Card({ title, icon, children }: { title: string; icon?: string; children: ReactNode }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 hover:border-blue-200 hover:bg-blue-50/30 transition-colors">
      {icon && <div className="text-2xl mb-2">{icon}</div>}
      <h4 className="font-semibold text-slate-900 mb-1.5">{title}</h4>
      <p className="text-sm text-slate-600 leading-relaxed">{children}</p>
    </div>
  )
}

// ── Divider ────────────────────────────────────────────────────────────────────

export function Divider() {
  return <hr className="my-8 border-slate-100" />
}

// ── UI Mockup (inline HTML mockup for UI illustrations) ───────────────────────

export function UIMockup({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <figure className="mb-6">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 overflow-hidden">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          {children}
        </div>
      </div>
      {label && <figcaption className="text-center text-xs text-slate-400 mt-2 italic">{label}</figcaption>}
    </figure>
  )
}
